import styled from 'styled-components';

import { Iframe } from 'modules/secureIframe';
import { HelpText } from 'ui/formField';
import { IconButton } from 'ui/button';
import {
  backgroundColors,
  caption,
  colors,
  fontSizes,
  fontWeights,
  fonts,
  largeShadow,
  monospace2,
  spacing,
  subtleShadow,
  textColors,
  zIndex,
} from 'ui/theme';
import { body2, heading4, overline } from 'ui/theme/typography';
import { DrawerFooter } from 'ui/fullScreen';
import { Skeleton } from 'ui/skeleton';
import { pxToRem } from 'utils/convert';

export const LayoutContentContainer = styled.div`
  /**
   the max-width here needs to include the padding so that the max-width of the content will match
   the normalPageMaxWidth as defined in zero-height
   max-width: ${pxToRem(1920)}; 
   */
  width: 100%;
`;

export const LayoutContentContainerFullWidth = styled.div`
  padding-left: ${spacing.double};
  padding-right: ${spacing.double};
  width: 100%;
`;

export const EditorContentContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  flex: 1;
  /** Temporary work-around until angular can be removed (which adds Proxima Nova to the body) */
  font-family: ${fonts.sansSerif};
  gap: ${spacing.plusHalf};
  justify-content: center;
  margin-bottom: ${spacing.double};
  margin-top: ${spacing.double};
  padding-bottom: ${spacing.plusHalf};

  @media screen and (max-width: ${pxToRem(893)}) {
    flex-direction: column;
    align-items: center;
  }
`;

export const AceEditorWrapper = styled.div<{ hasError: boolean }>`
  border-radius: ${spacing.quarter};
  margin-top: ${pxToRem(24)};
  overflow: hidden;
  ${({ hasError }) => (hasError ? `outline: ${spacing.single} solid ${colors.red100};` : '')}
  ${subtleShadow}
`;

export const AceEditorWrapperContainer = styled.div`
  position: relative;
`;

export const AceEditorError = styled.div`
  align-items: center;
  color: ${colors.red100};
  column-gap: ${pxToRem(3)};
  display: flex;
  font-size: ${fontSizes.s12};
  position: absolute;
  top: calc(100% + ${pxToRem(6)});
`;

export const DataBar = styled.div`
  align-items: center;
  column-gap: ${pxToRem(5)};
  display: flex;
  margin-top: ${spacing.quarter};
  > button {
    flex: 0 0 auto;
  }
  > :first-child {
    flex: 1 1 auto;
  }
  > div {
    color: ${colors.textSecondary};
  }
`;

export const NoDataFeeds = styled.div`
  align-items: center;
  border-radius: ${spacing.quarter};
  border: ${spacing.single} solid ${colors.grey15};
  color: ${colors.textSecondary};
  display: flex;
  flex-direction: column;
  font-size: ${fontSizes.s14};
  height: ${pxToRem(165)};
  justify-content: center;
  margin-top: ${pxToRem(14)};
  row-gap: ${pxToRem(16)};

  & > a {
    color: ${colors.green100};
  }
`;

export const SectionHeader = styled.div`
  color: ${colors.textPrimary};
  font-size: ${fontSizes.s20};
  font-weight: ${fontWeights.semiBold};
  &:not(:first-child) {
    margin-top: ${pxToRem(46)};
  }
`;

export const Container = styled.div`
  border-radius: ${spacing.half};
  flex-direction: row;
  flex: 1;
  max-width: ${pxToRem(893)};
  min-height: ${pxToRem(562)};
  &&.hasNullEmptyDataFeed{
    min-height: auto;
  }

  @media screen and (max-width: ${pxToRem(893)}) {
    max-width: 100%;
    width: 100%;
  }
`;

export const PreviewPaneContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  row-gap: ${spacing.plusHalf};
  max-width: ${pxToRem(1088)};
  @media screen and (max-width: ${pxToRem(893)}) {
    width: 100%;
  }
`;

export const RawDataHeader = styled.div`
  ${heading4};
`;

export const RawDataContainer = styled.div`
  background-color: ${colors.backgroundPrimary};
  border: ${spacing.single} solid transparent;
  border-radius: ${spacing.half};
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: ${spacing.plusHalf};
  position: relative;
  row-gap: ${spacing.plusHalf};
  z-index: ${zIndex.dropdown - 1};
`;

export const PreviewWithErrorContainer = styled(Container)`
  max-width: ${pxToRem(1088)};
`;

export const PreviewContainer = styled(Container)<{
  hasPreviewInfoMessage?: boolean;
  isPreviewWithData?: boolean;
}>`
  background-color: ${({ isPreviewWithData }) =>
    isPreviewWithData ? colors.backgroundPrimary : colors.backgroundSecondary};
  border: ${spacing.single} solid transparent;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: ${({ hasPreviewInfoMessage }) => hasPreviewInfoMessage && pxToRem(775)};
  position: relative;
  ${({ isPreviewWithData }) => (isPreviewWithData ? `z-index: ${zIndex.dropdown - 1}` : '')};
  max-width: ${pxToRem(1088)};
`;

export const PreviewHeaderWrapper = styled.div`
  padding-bottom: ${pxToRem(0)};
`;

export const PreviewHeaderContainer = styled.div`
  ${heading4};
  padding: ${spacing.plusHalf} ${spacing.plusHalf} 0 ${spacing.plusHalf};
  display: flex;
  justify-content: space-between;
`;

export const PreviewContent = styled(Iframe)<{ $height?: string }>`
  border: none;
  flex: 1 1 auto;
  padding-top: ${spacing.plusHalf};
  margin-bottom: ${spacing.plusHalf};
  height: ${({ $height }) => $height};
`;

export const PreviewContentContentContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: ${spacing.plusHalf};
  padding-bottom: ${spacing.plusHalf};
  position: relative;
`;

export const PreviewControlContainer = styled.div`
  align-items: center;
  column-gap: ${spacing.half};
  display: flex;
  position: absolute;
  right: ${pxToRem(10)};
  top: ${pxToRem(20)};
`;

export const PreviewControls = styled.div`
  align-items: center;
  column-gap: ${spacing.half};
  display: flex;
  flex: 0 0 auto;
  justify-content: flex-start;
`;

export const PreviewControlsMsg = styled.div`
  align-items: center;
  column-gap: ${spacing.half};
  display: flex;
  flex: 1 1 auto;
  justify-content: flex-end;
`;

export const PreviewMessage = styled.div`
  align-items: center;
  color: ${colors.textSecondary};
  display: flex;
  flex: 1 1 auto;
  font-size: ${fontSizes.s12};
  justify-content: flex-end;

  & > * + * {
    margin-left: ${spacing.half};

    &:last-child {
      margin-left: ${spacing.quarter};
    }
  }
`;

export const PreviewFooter = styled.div`
  border-top: ${spacing.single} solid ${colors.grey15};
  column-gap: ${spacing.half};
  display: flex;
  flex: 0 0 auto;
  padding: ${spacing.plusHalf};
`;

export const PreviewShortenAndTrackContainer = styled.div`
  color: ${colors.textSecondary};
  font-size: ${fontSizes.s12};
  text-align: center;
  padding-bottom: ${spacing.plusHalf};
`;

export const PreviewRefreshButton = styled(IconButton)`
  position: absolute;
  right: ${pxToRem(10)};
  top: ${pxToRem(10)};
`;

export const ByteSizeWarning = styled.div`
  align-items: center;
  color: ${colors.warning100};
  display: flex;
`;

export const SettingsContainer = styled(Container)<{ isEditMode: boolean }>`
  border: ${(props) =>
    props.isEditMode
      ? `${spacing.eighth} solid ${colors.green50}`
      : `${spacing.single} solid ${colors.grey15}`};
  ${({ isEditMode }) => (isEditMode ? largeShadow : ``)}
  height: fit-content;
  overflow: hidden;
  position: relative;
  @media screen and (max-width: ${pxToRem(1775)}) and (min-width: ${pxToRem(1393)}) {
    max-width: ${pxToRem(720)};
  }
`;

export const SettingsLoader = styled(Skeleton)`
  inset: 0;
  position: absolute;
  z-index: 1;
`;

export const SettingsWrapper = styled.div`
  padding: ${spacing.plusHalf};
`;

export const SettingsHeader = styled.div`
  ${heading4};
  display: flex;
  justify-content: space-between;
`;

export const SettingsSectionHeader = styled.div`
  ${overline};
  color: ${textColors.textSecondary};
  margin-bottom: ${spacing.double};
  &:not(:first-child) {
    margin-top: ${spacing.plusHalf};
  }
`;

export const SettingsReadOnlyText = styled.div<{ isUnset?: boolean; $hasError?: boolean }>`
  ${body2};
  ${({ isUnset, $hasError }) => {
    if ($hasError) {
      return `color: ${colors.red100}`;
    } else if (isUnset) {
      return `color: ${colors.textSecondary};`;
    }
    return '';
  }};
  display: flex;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing.values.eighth};
  max-width: ${({ theme }) => theme.spacing.utils.pxToRem(352)};
  overflow: hidden;
  word-break: break-word;
  white-space: break-spaces;
`;

export const SettingsReadOnlyUrl = styled(SettingsReadOnlyText)`
  overflow-wrap: anywhere;
`;

export const SettingsReadOnlyDisclaimer = styled(SettingsReadOnlyText)`
  padding-top: ${spacing.plusHalf};
`;

export const SettingsFieldMoreDetails = styled.div`
  ${caption};
  font-family: ${fonts.monospaced};
  color: ${colors.textSecondary};
  line-height: ${fontSizes.s24};
  text-align: right;
`;

export const SettingsFieldMoreDetailInRow = styled.div`
  ${caption};
  font-family: ${fonts.monospaced};
  color: ${colors.textSecondary};
  line-height: ${fontSizes.s24};
  word-break: break-word;
`;

export const DetailsSaveButtonWrapper = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

export const UnsavedChangesMessage = styled.div`
  ${caption};
  color: ${colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${spacing.threeEights};
`;

/** Used for displaying example links  */
export const LinkContainer = styled.div`
  background-color: ${backgroundColors.backgroundSecondary};
  ${monospace2};
  font-size: ${pxToRem(10)};
  line-height: ${fontSizes.s16};
  color: ${colors.textSecondary};
  padding: ${spacing.half};
  margin-top: ${spacing.threeQuarter};
  overflow-wrap: anywhere;
`;

export const LinkDescription = styled(HelpText)`
  margin-top: ${spacing.half};
`;

export const TwoColumnFieldDisplay = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const TwoRowFieldDisplay = styled.div`
  display: flex;
  flex-direction: column;
`;

/** Used for exanded settings fields indented to align with checkbox description */
export const FieldExpanded = styled.div`
  margin-left: ${spacing.plusHalf};
`;

export const FormFieldTooltipContent = styled.div`
  text-align: center;
`;

export const StyledDrawerFooter = styled(DrawerFooter)`
  justify-content: flex-start;
  gap: ${spacing.half};
`;
